/** Pure video MIME table and selection with an injected probe; the result must be used for both the recorder and the final `Blob` to avoid Safari's blob-MIME mismatch. */
import type { AudioMimeEnv } from "./audio-mime";

/** Priority-ordered video MIME candidates. */
export const VIDEO_MIME_CANDIDATES = [
  "video/mp4;codecs=h264,aac", // Safari 15+
  "video/mp4",
  "video/webm;codecs=vp9,opus", // Chrome / Edge preferred
  "video/webm;codecs=vp8,opus",
  "video/webm",
  "video/ogg",
  "video/x-matroska;codecs=avc1",
] as const;

/** Browser hints for video codec preference branches (same shape as audio). */
export type VideoMimeEnv = AudioMimeEnv;

/** Resolve the best video MIME: Firefox prefers VP8 (more reliable hardware decode), Safari prefers h264/aac, else the first supported or `video/mp4`. */
export function negotiateVideoMime(env: VideoMimeEnv): string {
  const probe = env.isTypeSupported;
  if (!probe) return "video/mp4"; // SSR / no MediaRecorder

  // Firefox: VP8 over VP9 for hardware-decode reliability.
  if (env.isFirefox && probe("video/webm;codecs=vp8,opus")) {
    return "video/webm;codecs=vp8,opus";
  }

  if (env.isSafari) {
    if (probe("video/mp4;codecs=h264,aac")) return "video/mp4;codecs=h264,aac";
    return "video/mp4";
  }

  for (const candidate of VIDEO_MIME_CANDIDATES) {
    if (probe(candidate)) return candidate;
  }

  return "video/mp4"; // final fallback
}
